import React from 'react';
import { useSelector } from 'react-redux';

import { FormHelperText } from '@material-ui/core';
import {
  KeyboardDateTimePicker,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { useField } from '@unform/core';
import { isEqual, includes, isFunction, isNull } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import { canURL, t } from '~/helpers';

import * as S from './styles';

export default function DateTime({
  name,
  label,
  disabled,
  time,
  getValue,
  serialize,
  nullable,
  isGrid,
  ...rest
}) {
  const inputRef = React.useRef(null);
  const permission = isEqual(canURL(), { read: true });

  const [picker, setPicker] = React.useState(nullable ? null : moment());
  const [inputError, setInputError] = React.useState(false);

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);
  const { language } = useSelector((state) => state.user);

  // Modify format date and time component
  const formatDateTime = React.useMemo(() => {
    if (language === 'pt-BR' || language === 'es-CL' || language === 'es-ES') {
      if (time) return 'DD/MM/YYYY HH:mm:ss';

      return 'DD/MM/YYYY';
    }

    if (time) return 'YYYY-MM-DD HH:mm:ss';

    return 'YYYY-MM-DD';
  }, [language, time]);

  // Change Picker
  const handleChangePicker = React.useCallback((value) => {
    setPicker(value);
  }, []);

  // Toggle error component
  const handleChangeInput = React.useCallback(() => {
    if (isNull(picker) || !picker.isValid()) {
      getValue(null);
    } else {
      getValue(
        isFunction(serialize)
          ? serialize(picker)
          : picker.format(formatDateTime)
      );
    }
    inputRef.current.value = isFunction(serialize)
      ? serialize(picker)
      : picker.format(formatDateTime);
  }, [picker]);

  React.useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [fieldName, registerField]);

  React.useEffect(() => {
    if (defaultValue) {
      const convertDate = moment(defaultValue, 'DD/MM/YYYY HH:mm:ss');

      inputRef.current.value = isFunction(serialize)
        ? serialize(convertDate)
        : convertDate.format(formatDateTime);

      setPicker(convertDate);
    } else {
      inputRef.current.value = isFunction(serialize)
        ? serialize(moment())
        : moment().format(formatDateTime);
    }
  }, [defaultValue, formatDateTime]);

  React.useEffect(() => {
    if (
      picker &&
      !includes(
        ['Data inválida', 'Invalid date'],
        picker.format(formatDateTime)
      )
    ) {
      handleChangeInput();
    } else {
      inputRef.current.value = null;
    }
  }, [picker]);

  React.useEffect(() => {
    if (disabled) clearError();
  }, [disabled]);

  return (
    <S.Wrapper>
      {time ? (
        <KeyboardDateTimePicker
          fullWidth
          error={!!error || inputError}
          value={picker}
          ampm={false}
          onChange={handleChangePicker}
          label={label}
          inputVariant="outlined"
          size="small"
          cancelLabel={t('s.geral.botaocancelar')}
          {...rest}
          disabled={(permission || disabled) && !isGrid}
          format={formatDateTime}
          onError={(err) => setInputError(err === 'Invalid Date Format')}
          onFocus={clearError}
          readOnly={disabled}
        />
      ) : (
        <KeyboardDatePicker
          fullWidth
          error={!!error || inputError}
          value={picker}
          onChange={handleChangePicker}
          label={label}
          inputVariant="outlined"
          size="small"
          cancelLabel={t('s.geral.botaocancelar')}
          {...rest}
          disabled={(permission || disabled) && !isGrid}
          format={formatDateTime}
          onError={(err) => setInputError(err === 'Invalid Date Format')}
          onFocus={clearError}
          readOnly={disabled}
        />
      )}

      <input type="hidden" ref={inputRef} />
      {!!error && <FormHelperText error>{error}</FormHelperText>}
    </S.Wrapper>
  );
}

DateTime.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  time: PropTypes.bool,
  getValue: PropTypes.func,
  serialize: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  nullable: PropTypes.bool,
  isGrid: PropTypes.bool,
  minDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  maxDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  shouldDisableDate: PropTypes.func,
  disableMaskedInput: PropTypes.bool,
  helperText: PropTypes.string,
};

DateTime.defaultProps = {
  disabled: false,
  time: false,
  getValue: () => undefined,
  serialize: false,
  nullable: false,
  isGrid: false,
};
